import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Checkbox,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	FieldLabel,
	Separator,
} from "@monbits/ui";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle2, MoreVertical, Send } from "lucide-react";
import * as React from "react";
import { showToast } from "src/lib/toast-helper";
import notificationQueries from "src/queries/notification-queries";
import type { MerchantNotification } from "src/types";
import {
	EVENT_DEFAULTS,
	EVENT_GROUPS,
	EVENT_LABELS,
	eventsEqual,
} from "./events";
import { humanizeTelegramError } from "./telegram-error";

interface Props {
	notification: MerchantNotification;
	onReconfigure: () => void;
	onDisconnected: () => void;
}

const TelegramConnectedView: React.FC<Props> = ({
	notification,
	onReconfigure,
	onDisconnected,
}) => {
	const initialEvents = React.useMemo(
		() => ({ ...EVENT_DEFAULTS, ...notification.events }),
		[notification.events],
	);
	const [events, setEvents] = React.useState(initialEvents);
	const [lastTestAt, setLastTestAt] = React.useState<Date | null>(null);
	const [lastTestOk, setLastTestOk] = React.useState<boolean | null>(null);
	const [disconnectOpen, setDisconnectOpen] = React.useState(false);

	React.useEffect(() => {
		setEvents(initialEvents);
	}, [initialEvents]);

	const testNotification = notificationQueries.testNotification();
	const upsertNotification = notificationQueries.upsertNotification();
	const deleteNotification = notificationQueries.deleteNotification();

	const isDirty = !eventsEqual(events, initialEvents);

	const botTokenMasked = notification.config?.bot_token ?? "";
	const chatId = notification.config?.chat_id ?? "";

	const handleTest = async () => {
		try {
			const result = await testNotification.mutateAsync("telegram");
			setLastTestAt(new Date());
			setLastTestOk(result.ok);
			if (result.ok) {
				showToast.success({
					message: "Test sent",
					description: "Check your Telegram chat for the message.",
				});
			} else {
				showToast.error({
					message: "Test failed",
					description: humanizeTelegramError(result.error ?? ""),
				});
			}
		} catch {
			setLastTestAt(new Date());
			setLastTestOk(false);
			showToast.error({
				message: "Test failed",
				description: "Unable to reach the server. Try again in a moment.",
			});
		}
	};

	const toggleEvent = (key: string, checked: boolean) => {
		setEvents((prev) => ({ ...prev, [key]: checked }));
	};

	const handleSaveEvents = async () => {
		try {
			await upsertNotification.mutateAsync({
				channel: "telegram",
				params: {
					enabled: notification.enabled,
					config: notification.config,
					events,
				},
			});
			showToast.success({
				message: "Events updated",
				description: "Your notification preferences have been saved.",
			});
		} catch {
			showToast.error({
				message: "Save failed",
				description: "Unable to save your event preferences.",
			});
		}
	};

	const handleDisconnect = async () => {
		try {
			await deleteNotification.mutateAsync("telegram");
			showToast.success({
				message: "Telegram disconnected",
				description: "You can reconnect any time.",
			});
			setDisconnectOpen(false);
			onDisconnected();
		} catch {
			showToast.error({
				message: "Disconnect failed",
				description: "Unable to disconnect. Try again in a moment.",
			});
		}
	};

	return (
		<>
			<Card>
				<CardHeader>
					<div className="flex items-start justify-between gap-3">
						<div className="flex items-start gap-3">
							<Send
								className="h-5 w-5 mt-0.5 text-muted-foreground"
								aria-hidden
							/>
							<div>
								<CardTitle>Telegram</CardTitle>
								<CardDescription>
									Send order, payment, and withdrawal updates to a Telegram chat
									or channel.
								</CardDescription>
							</div>
						</div>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="icon" aria-label="More actions">
									<MoreVertical className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onSelect={onReconfigure}>
									Reconfigure
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									variant="destructive"
									onSelect={(e) => {
										e.preventDefault();
										setDisconnectOpen(true);
									}}
								>
									Disconnect
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="rounded-lg border bg-muted/30 p-4 space-y-3">
						<div className="flex items-center gap-2 text-sm">
							<CheckCircle2 className="h-4 w-4 text-primary" aria-hidden />
							<span className="font-medium">Connected</span>
						</div>
						<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
							<dt className="text-muted-foreground">Bot token</dt>
							<dd className="font-mono">{botTokenMasked}</dd>
							<dt className="text-muted-foreground">Chat ID</dt>
							<dd className="font-mono">{chatId}</dd>
							{lastTestAt && (
								<>
									<dt className="text-muted-foreground">Last test</dt>
									<dd>
										{formatDistanceToNow(lastTestAt, { addSuffix: true })}
										<span
											className={
												lastTestOk
													? "ml-2 text-primary"
													: "ml-2 text-destructive"
											}
										>
											· {lastTestOk ? "succeeded" : "failed"}
										</span>
									</dd>
								</>
							)}
						</dl>
						<div className="pt-1">
							<Button
								variant="outline"
								size="sm"
								onClick={handleTest}
								disabled={testNotification.isPending}
							>
								{testNotification.isPending ? "Sending…" : "Send test message"}
							</Button>
						</div>
					</div>

					<Separator />

					<div className="space-y-4">
						<div>
							<h3 className="text-sm font-semibold">Events to notify</h3>
							<p className="text-xs text-muted-foreground mt-1">
								Choose which status changes send a Telegram message.
							</p>
						</div>
						<div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
							{EVENT_GROUPS.map((group) => (
								<div key={group.title} className="space-y-3">
									<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
										{group.title}
									</p>
									<div className="space-y-2">
										{group.events.map((key) => (
											<div key={key} className="flex items-center gap-2">
												<Checkbox
													id={`event-${key}`}
													checked={events[key] ?? false}
													onCheckedChange={(checked) =>
														toggleEvent(key, Boolean(checked))
													}
												/>
												<FieldLabel
													htmlFor={`event-${key}`}
													className="text-sm font-normal cursor-pointer"
												>
													{EVENT_LABELS[key]}
												</FieldLabel>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
						<div className="flex justify-end pt-2">
							<Button
								onClick={handleSaveEvents}
								disabled={!isDirty || upsertNotification.isPending}
							>
								{upsertNotification.isPending
									? "Saving…"
									: "Save event changes"}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			<Dialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Disconnect Telegram?</DialogTitle>
						<DialogDescription>
							Stop sending Telegram notifications? You can reconnect any time by
							pasting the bot token and chat ID again.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setDisconnectOpen(false)}
							disabled={deleteNotification.isPending}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={handleDisconnect}
							disabled={deleteNotification.isPending}
						>
							{deleteNotification.isPending ? "Disconnecting…" : "Disconnect"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default TelegramConnectedView;
